import { FormLabel, TextField } from "@mui/material";
import { ChangeEvent, HTMLInputTypeAttribute } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ERROR_TYPE, translateError } from "@/utils";
import { FONT_STACK } from "@/theme/tokens";

interface IFormTextField {
	label: string;
	errorMessage?: string;
	startAdornment?: JSX.Element;
	endAdornment?: JSX.Element;
	containerClassName?: string;
	formRegister?: UseFormRegisterReturn;
	autoComplete?: string;
	type?: HTMLInputTypeAttribute;
	onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	placeHolder?: string;
	required?: boolean;
}

export default function FormTextField({
	label,
	errorMessage,
	startAdornment,
	containerClassName,
	formRegister,
	autoComplete,
	type,
	onChange,
	placeHolder,
	required = true,
	endAdornment,
}: IFormTextField) {
	const { t } = useTranslation();

	return (
		<div className={`space-y-1 mb-2 relative ${containerClassName}`}>
			<FormLabel
				component="label"
				sx={{
					color: "#11110F",
					fontSize: "14px",
					fontWeight: "500",
				}}
			>
				{label} {required && <span className="text-red-500">*</span>}
			</FormLabel>
			<TextField
				fullWidth
				InputProps={{
					autoComplete: autoComplete,
					sx: {
						fontSize: "14px",
						color: "#11110F",
						fontWeight: "400",
						lineHeight: "1.2rem",
						fontFamily: FONT_STACK,
						"&:focus-visible": {
							outline: "none",
							inset: "none",
							border: "none",
						},
						paddingY: "5px",
						"& input": {
							paddingY: "5px",
							"&::placeholder": {
								color: "#686761",
								fontSize: "14px",
								fontWeight: "400",
								fontFamily: FONT_STACK,
								opacity: 1,
							},
						},
						"&:focus": {
							outline: "none",
							boxShadow: "none",
							inset: "none",
							border: "none",
						},
						backgroundColor: "#fff",
					},
					startAdornment,
					endAdornment,
				}}
				autoComplete={autoComplete}
				className="autofill:bg-white autofill:p-0 autofill:text-[14px] autofill:font-normal autofill:text-ink "
				error={!!errorMessage}
				placeholder={
					placeHolder ??
					t("basics.enterSomething", {
						something: t(`${label}`),
					})
				}
				sx={{
					"& .MuiOutlinedInput-root": {
						"& fieldset": {
							border: "1px solid #E8E6E2",
						},
						"&:hover fieldset": {
							border: "1px solid #B8431A70",
						},
						"&.Mui-focused fieldset": {
							border: "none",
							outline: "none",
							inset: "none",
						},
						borderRadius: "12px",
						"&.Mui-error fieldset": {
							border: "none !important",
							outline: "none !important",
							inset: "none !important",
						},
						fontFamily: FONT_STACK,
					},
				}}
				type={type}
				variant="outlined"
				{...(formRegister ?? {})}
				onChange={(e) => {
					formRegister?.onChange(e);
					onChange?.(e);
				}}
			/>
			<p className=" text-[12px] text-danger">
				{translateError[errorMessage as unknown as ERROR_TYPE]}
			</p>
		</div>
	);
}
